import { Icon } from '@/modules/Icon';
import { Visual } from '@/modules/Visual/Index';
import { useTextarea } from '@/modules/Textarea/hooks/useTextarea';
import { TextareaProps } from '@/shared/types/textarea';
import { cn } from '@/shared/utils';
import { useMemo } from 'react';
import { faFile } from '@fortawesome/pro-regular-svg-icons';

/**
 * Textarea - Componente de área de texto versátil y configurable
 *
 * Componente de textarea que soporta labels, mensajes de error, slots personalizados,
 * contador de caracteres, límite de longitud y estados controlados/no controlados.
 *
 * @example
 * ```tsx
 * // Textarea simple
 * <Textarea
 *   label="Descripción"
 *   value={description}
 *   onChange={(e) => setDescription(e.target.value)}
 *   placeholder="Escribe una descripción..."
 * />
 *
 * // Textarea con límite de caracteres
 * <Textarea
 *   label="Comentario"
 *   value={comment}
 *   onChange={(e) => setComment(e.target.value)}
 *   maxLength={500}
 *   helperText="Máximo 500 caracteres"
 * />
 *
 * // Textarea con error
 * <Textarea
 *   label="Mensaje"
 *   value={message}
 *   onChange={(e) => setMessage(e.target.value)}
 *   error="Este campo es requerido"
 * />
 * ```
 *
 * @see {@link TextareaProps} para la lista completa de props
 */
export const Textarea = ({
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder = '',
  className = '',
  containerClassName = '',
  leftSlot = null,
  rightSlot = null,
  disabled = false,
  error = '',
  helperText = '',
  maxLength = 0,
  label = '',
  fullWidth = true,
}: TextareaProps) => {
  const { isFocused, setIsFocused, count, handleChange, handlePaste } = useTextarea({
    maxLength,
    onChange,
    value,
  });

  const labelClasses = useMemo(() => {
    const base = 'ui-text-body-sm-regular ui-font-body-sm-bold';
    return error ? `${base} ui-text-danger` : `${base} ui-text-primary`;
  }, [error]);

  return (
    <div
      className={cn(
        'ui-imj ui-relative ui-flex ui-flex-col ui-gap-1',
        fullWidth ? 'ui-w-full' : 'ui-w-fit',
      )}
    >
      {label && (
        <div className="ui-flex ui-flex-row ui-items-center ui-gap-1 ui-leading-tight">
          {label && <label className={labelClasses}>{label}</label>}
        </div>
      )}
      <div
        className={cn(
          'ui-relative ui-flex ui-h-full ui-min-h-5 ui-w-full ui-flex-col',
          containerClassName,
        )}
      >
        {leftSlot && (
          <Icon
            name={leftSlot}
            className="ui-absolute ui-left-2 ui-top-2"
            size="sm"
            color={disabled ? 'tertiary' : error ? 'danger' : isFocused ? 'brand' : 'tertiary'}
          />
        )}

        <textarea
          placeholder={placeholder}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus?.(e);
          }}
          value={value || ''}
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur?.(e);
          }}
          disabled={disabled}
          rows={1}
          className={cn(
            'ui-textarea ui-min-h-23 ui-w-full ui-rounded-sm ui-border-1 ui-bg-surface-default ui-p-8 ui-pt-6 ui-text-body-sm-regular focus:ui-outline-none',
            maxLength > 0 ? 'ui-pb-26' : 'ui-pb-8',
            leftSlot ? 'ui-pl-28' : 'ui-pl-8',
            rightSlot ? 'ui-pr-28' : 'ui-pr-8',
            disabled
              ? 'ui-border-disabled ui-bg-surface-disabled ui-text-disabled'
              : error
                ? 'ui-border-danger ui-bg-surface-danger ui-text-danger placeholder:ui-text-danger'
                : 'ui-border-default ui-text-primary placeholder:ui-text-tertiary focus:ui-border-brand',
            className,
          )}
        />

        {rightSlot && (
          <Icon
            name={rightSlot}
            className="ui-absolute ui-right-2 ui-top-2"
            color={disabled ? 'tertiary' : error ? 'danger' : isFocused ? 'brand' : 'secondary'}
          />
        )}

        {maxLength > 0 && (
          <div
            className={cn(
              'ui-absolute ui-flex ui-w-full ui-items-center ui-gap-4 ui-px-8',
              helperText && error
                ? 'ui-bottom-[50px]'
                : helperText || error
                  ? 'ui-bottom-[29px]'
                  : 'ui-bottom-2',
            )}
          >
            <Icon
              name={faFile}
              color={disabled ? 'tertiary' : error ? 'danger' : isFocused ? 'brand' : 'secondary'}
              size="xs"
            />
            <p className="ui-text-body-xs-regular ui-leading-none ui-text-tertiary">
              {count}/{maxLength}
            </p>
          </div>
        )}

        {helperText && (
          <p className="ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs ui-text-tertiary">
            {helperText}
          </p>
        )}
        {error && (
          <p className="ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs ui-text-danger">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};
